/**
 * schematic_dealer_lookup.ts — the client's view of the schematic dealers:
 * which settlement square the player stands at (or the nearest one), the
 * dealer's face (the same pure pick the server makes over the replicated
 * npc_agent rows) and THIS season's board (the same pure offers the server
 * sells). Nothing here is authoritative — buySchematic re-derives it all.
 */
import { settlementSites, type SettlementSite } from '@shared/settlements.js';
import { settlementNamesFor } from './settlement_names.js';
import { civilizationOf, type Civilization } from './biome_settlements.js';
import { civOfSettlementRowC } from './civ_row.js';
import { dealerEpoch, dealerOffersFor, pickDealer, DEALER_RANGE_M, type DealerOffer } from './schematic_dealers.js';
import { nameFromTables } from './versona_names.js';

export interface DealerView {
  settlementId: bigint;
  site: SettlementSite;
  settlementName: string;
  distanceM: number;
  inRange: boolean;
  dealerId: bigint | null;
  dealerName: string;
  offers: DealerOffer[];
  epoch: number;
}

interface WorldLite { kind: string; terrainSeed: string }
interface AgentLite { versonaId: bigint; settlementId: bigint; job: string }

let cacheSeed = '';
let cacheSites: SettlementSite[] = [];
let cacheNames = new Map<number, string>();

function sitesFor(seed: string): { sites: SettlementSite[]; names: Map<number, string> } {
  if (seed !== cacheSeed) {
    cacheSeed = seed;
    cacheSites = settlementSites(seed);
    const civOf = (siteSeed: number, siteId?: number): Civilization =>
      siteId === undefined ? civilizationOf(siteSeed) : civOfSettlementRowC(BigInt(siteId + 1), siteSeed);
    cacheNames = settlementNamesFor(cacheSites, civOf);
  }
  return { sites: cacheSites, names: cacheNames };
}

function tbl<T>(conn: unknown, name: string): T[] {
  try {
    const t = (conn as { db: Record<string, { iter(): Iterable<unknown> } | undefined> }).db[name];
    return t ? ([...t.iter()] as T[]) : [];
  } catch { return []; }
}

/** The nearest city/town with a dealer (hamlets have none). */
export function nearestDealer(conn: unknown, x: number, z: number, nowMs: number): DealerView | null {
  const commons = tbl<WorldLite>(conn, 'world').find((w) => w.kind === 'commons');
  if (!commons) return null;
  const { sites, names } = sitesFor(commons.terrainSeed);
  let best: SettlementSite | null = null, bestD = Infinity;
  for (const s of sites) {
    if (s.tier === 'hamlet') continue;
    const d = Math.hypot(s.centerX - x, s.centerZ - z);
    if (d < bestD) { bestD = d; best = s; }
  }
  if (!best) return null;
  const settlementId = BigInt(best.id + 1);
  const roster = tbl<AgentLite>(conn, 'npcAgent').filter((a) => a.settlementId === settlementId)
    .map((a) => ({ versonaId: a.versonaId, job: a.job }));
  const dealerId = pickDealer(roster);
  const epoch = dealerEpoch(nowMs);
  return {
    settlementId, site: best,
    settlementName: names.get(best.id) ?? `settlement ${best.id + 1}`,
    distanceM: bestD, inRange: bestD <= DEALER_RANGE_M,
    dealerId,
    dealerName: dealerId !== null ? nameFromTables(conn, dealerId) : 'the schematic dealer',
    offers: dealerOffersFor(best.siteSeed, best.tier, epoch),
    epoch,
  };
}

/** Seconds until the season's board turns (the epoch boundary). */
export function boardTurnsInS(nowMs: number): number {
  const epochMs = 6 * 3600 * 1000;
  return Math.max(0, Math.round(((Math.floor(nowMs / epochMs) + 1) * epochMs - nowMs) / 1000));
}
